"use strict";

import React, {useState, useEffect} from 'react';
import { render, unmountComponentAtNode } from 'react-dom';
import axios from "../axiosWrapper";
import objectPath from "object-path";
import ProductGallery from "./component/ProductGallery";
import preloadImages from "../preloadImages";
import size from "../size";

export default ()=>{
    const galleryEl = document.getElementById('product-detail__slides__component');
    
    // State management for gallery updates
    let currentGalleryInstanceContainer = null;
    let currentlyDisplayedProductId = null;
    let isLoadingGallery = false;

    let defaultImage = [];
    let supportingImages = [];

    const adjustContainerEl = (mainEl=null, serverRenderedDisplay=null,) => {
        return new Promise((resolve, reject)=>{
            if(!!mainEl){
                mainEl.select
                mainEl.style.minHeight = `${size.outerHeight(mainEl)}px`;
                mainEl.style.position = `relative`;
                if(!!serverRenderedDisplay){
                    serverRenderedDisplay.style.position = 'absolute';
                    serverRenderedDisplay.style.top = '0';
                    serverRenderedDisplay.style.left = '0';
                    serverRenderedDisplay.style.right = '0';
                    serverRenderedDisplay.style.marginBottom = '1.25rem';
                }
                mainEl.offsetHeight;
                resolve();
            }
            else
                reject();
        });
    }

    const newGalleryContainer = (mainEl=null)=>{
        const containerEl = document.createElement('div');
        containerEl.className = 'product-detail__slides__component__container';
        containerEl.style.marginBottom = '1.25rem';
        mainEl.appendChild(containerEl);
        return containerEl;
    }

    const cleanUpContainerEl = (mainEl=null, serverRenderedDisplay=null,) => {
        setTimeout(()=>{
            if(!!mainEl)
                mainEl.style.minHeight = 'auto';
            if(!!serverRenderedDisplay)
                serverRenderedDisplay.parentNode.removeChild(serverRenderedDisplay);
        }, 500);
    }

    /**
     * Load and render the product gallery for a specific product
     * @param {string} productId - The ID of the product to load images for
     * @returns {Promise} - A promise that resolves when the gallery is loaded and rendered
     */
    const loadAndRenderGallery = (productId) => {
        return new Promise((resolve, reject) => {
            // Skip if already loading or same product ID
            if (isLoadingGallery || productId === currentlyDisplayedProductId) {
                resolve();
                return;
            }

            // Set loading state
            isLoadingGallery = true;
            
            // Fetch images for the product
            axios.get(`/products/${encodeURIComponent(productId)}/images/`)
                .then((response) => {
                    const defaultImage = objectPath.get(response, 'data.default_image', {});
                    const supportingImages = objectPath.get(response, 'data.supporting_images', []);
                    
                    if (!!defaultImage.enlarged_name) {
                        const initialPath = '/assets/img/products/large/';
                        const imagesToPreload = [`${initialPath}${defaultImage.enlarged_name}`];
                        
                        if (!!supportingImages && supportingImages.length) {
                            supportingImages.slice(0, 2).forEach((img) => {
                                !!img.enlarged_name && imagesToPreload.push(`${initialPath}${img.enlarged_name}`);
                            });
                        }
                        
                        // Preload images then render gallery
                        preloadImages(imagesToPreload)
                            .finally(() => {
                                if (galleryEl) {
                                    // Only remove the old gallery after we have the new images ready
                                    // Clean up existing gallery if it exists
                                    if (currentGalleryInstanceContainer) {
                                        // Unmount React component from the container
                                        unmountComponentAtNode(currentGalleryInstanceContainer);
                                        
                                        // Remove container from DOM
                                        if (currentGalleryInstanceContainer.parentNode) {
                                            currentGalleryInstanceContainer.parentNode.removeChild(currentGalleryInstanceContainer);
                                        }
                                    }
                                    
                                    // Create new container for the gallery
                                    const containerEl = newGalleryContainer(galleryEl);
                                    
                                    // Render the gallery
                                    render(
                                        <ProductGallery slides={[defaultImage].concat(supportingImages)} />,
                                        containerEl,
                                        () => {
                                            // Update state after successful render
                                            currentGalleryInstanceContainer = containerEl;
                                            currentlyDisplayedProductId = productId;
                                            isLoadingGallery = false;
                                            resolve();
                                        }
                                    );
                                } else {
                                    isLoadingGallery = false;
                                    reject(new Error('Gallery element not found'));
                                }
                            });
                    } else {
                        isLoadingGallery = false;
                        reject(new Error('No default image found for product'));
                    }
                })
                .catch((error) => {
                    console.error('Error loading product images:', error);
                    isLoadingGallery = false;
                    reject(error);
                });
        });
    };

    // Event listener for product selection changes
    document.addEventListener('newProductSelectedForGallery', (event) => {
        const productId = event.detail?.productId;
        
        if (productId) {
            loadAndRenderGallery(productId).catch(error => {
                console.error('Failed to update gallery:', error);
            });
        }
    });

    // Initial gallery load
    if (!!galleryEl) {
        const sliderElData = {...(galleryEl.dataset)};
        if (!!sliderElData.productId) {
            // For initial load, we need to handle the server-rendered display
            axios.get(`/products/${encodeURIComponent(sliderElData.productId)}/images/`).then((response) => {
                defaultImage = objectPath.get(response, 'data.default_image', {});
                supportingImages = objectPath.get(response, 'data.supporting_images', []);
                
                if (!!defaultImage.enlarged_name) {
                    const initialPath = '/assets/img/products/large/';
                    const imagesToPreload = [`${initialPath}${defaultImage.enlarged_name}`];
                    
                    if (!!supportingImages && supportingImages.length) {
                        supportingImages.slice(0, 2).forEach((img) => {
                            !!img.enlarged_name && imagesToPreload.push(`${initialPath}${img.enlarged_name}`);
                        });
                    }
                    
                    preloadImages(imagesToPreload).finally(() => {
                        const serverRenderedDisplay = !!galleryEl && galleryEl.querySelector('[data-images-list]');
                        adjustContainerEl(galleryEl, serverRenderedDisplay).finally(() => {
                            const containerEl = newGalleryContainer(galleryEl);
                            
                            render(
                                <ProductGallery slides={[defaultImage].concat(supportingImages)} />,
                                containerEl,
                                () => {
                                    // Update state after successful render
                                    currentGalleryInstanceContainer = containerEl;
                                    currentlyDisplayedProductId = sliderElData.productId;
                                    cleanUpContainerEl(galleryEl, serverRenderedDisplay);
                                }
                            );
                        });
                    });
                }
            });
        }
    }
};
